import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { fetchMenu } from "@/lib/api";
import { getErrorMessage } from "@/utils/errors";
import type { OrderType } from "@/constants/enums";
import type { MenuCategory, MenuItem } from "@/types";

interface MenuState {
  categories: MenuCategory[];
  items: MenuItem[];
  loading: boolean;
  error: string | null;
  selectedCategoryId: string | null;
  search: string;
}

const initialState: MenuState = {
  categories: [],
  items: [],
  loading: false,
  error: null,
  selectedCategoryId: null,
  search: "",
};

export const loadMenu = createAsyncThunk(
  "menu/load",
  async ({
    tenantId,
    orderType,
  }: {
    tenantId: string;
    orderType?: OrderType;
  }) => fetchMenu({ tenantId, orderType }),
);

const menuSlice = createSlice({
  name: "menu",
  initialState,
  reducers: {
    setSelectedCategory(state, action: PayloadAction<string | null>) {
      state.selectedCategoryId = action.payload ?? null;
    },
    setSearch(state, action: PayloadAction<string>) {
      state.search = action.payload ?? "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMenu.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMenu.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload?.categories ?? [];
        state.items = action.payload?.items ?? [];
        state.selectedCategoryId =
          state.selectedCategoryId ?? state.categories[0]?._id ?? null;
      })
      .addCase(loadMenu.rejected, (state, action) => {
        state.loading = false;
        // `error` is the flag that separates "this request FAILED" from "this
        // tenant has no items" — both render the same skeleton screen, and
        // without it the page cannot tell you which it is looking at. The
        // string is the original's own extractor (`iC`), with the thrown
        // message as its fallback so the developer-facing detail survives.
        state.error = getErrorMessage(action.error, action.error?.message);
      });
  },
});

export const { setSelectedCategory, setSearch } = menuSlice.actions;
export default menuSlice.reducer;
